/**
 * Pillar F.4 — the body, split at quoted-history boundaries: latest message expanded, earlier ones
 * behind "Show earlier messages (N)" with a per-segment header.
 *
 * MOVED here from `crm/CrmMessageDetail.tsx` (timeline-inline-message-spec §6) with its code
 * unchanged, so the message page, the modal and the entity timeline's inline row render one
 * message one way. `CrmMessageDetail` re-exports it from its old path, so no caller had to move.
 *
 * ⚠ It keeps importing `crm/crm.module.css`. That is deliberate, not laziness: `.msgBody`,
 * `.msgBodyText`, `.msgBodyFrame` and the four `.thread*` rules are used by NOTHING else, and
 * copying them into a second stylesheet would be two definitions of one look, free to drift, in
 * exchange for a tidier import graph. The rendered output being byte-identical is what gate G1
 * (`CrmMessageModal.test.tsx` green with zero edits) actually proves.
 *
 * HTML bodies are untouched: they keep going to the sandboxed iframe exactly as before. The splitter
 * is a PLAIN-TEXT heuristic and the ruling is explicit that no HTML parsing is added here.
 */
import { useState } from 'react';

import { segmentLabel, splitEmailThread } from '../crm/emailThread';
import styles from '../crm/crm.module.css';

export function ThreadedBody({ text }: { text: string }) {
  const [showEarlier, setShowEarlier] = useState(false);
  const [open, setOpen] = useState<number[]>([]);

  if (/^\s*</.test(text)) {
    return (
      <div className={styles.msgBody}>
        <iframe className={styles.msgBodyFrame} title="Email body" sandbox="" srcDoc={text} />
      </div>
    );
  }

  const segments = splitEmailThread(text);
  // One segment = no confident boundary (or a body that fails open) → today's rendering, verbatim.
  if (segments.length <= 1) {
    return <div className={styles.msgBody}><pre className={styles.msgBodyText}>{text}</pre></div>;
  }
  const earlier = segments.slice(1);
  return (
    <div className={styles.msgBody}>
      <pre className={styles.msgBodyText}>{segments[0].text}</pre>
      {!showEarlier ? (
        <button type="button" className={styles.threadToggle} onClick={() => setShowEarlier(true)}>
          {`Show earlier messages (${earlier.length})`}
        </button>
      ) : (
        <>
          <button type="button" className={styles.threadToggle} onClick={() => setShowEarlier(false)}>
            Hide earlier messages
          </button>
          <ul className={styles.threadList}>
            {earlier.map((seg, i) => {
              const isOpen = open.includes(i);
              return (
                <li key={i} className={styles.threadItem}>
                  <button
                    type="button"
                    className={styles.threadSegHead}
                    aria-expanded={isOpen}
                    onClick={() =>
                      setOpen((s) => (s.includes(i) ? s.filter((k) => k !== i) : [...s, i]))}
                  >
                    <span className={styles.threadCaret}>{isOpen ? '▾' : '▸'}</span>
                    {segmentLabel(seg)}
                  </button>
                  {isOpen && <pre className={styles.msgBodyText}>{seg.text}</pre>}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

export default ThreadedBody;
